import { hashWords, toUnitFloat } from "daedalus-core";
import { LevelUpSound, Stinger } from "odyssey-shared";
import { MAX_RAYS, MAX_SPARKS, type SparkPath, type VfxDefinition, type VfxLayer } from "../definition.ts";

/**
 * The `levelUp` vfx (P2.4-T41, the author's option C; ADR-083's note of 2026-10-24 and ADR-134): 1800 ms. A dark ring
 * closes on the feet and the aura rises for 200 ms; at 200 ms a bright ring spreads to 44 art pixels, twelve rays go
 * out, 24 sparks spray out and up, and a full-height pillar stands on the feet and narrows from 800 ms to 1100 ms; a sign
 * rises over the head from 400 ms, blinks once and goes at 1700 ms; the page's own character gets the edge glow from 200
 * ms to 1300 ms. Others see it. Its owner hears the stinger and others the chime. The sparks' flights come from the
 * shared seeded hash, so a spark is the same everywhere.
 */

const SPARK_SEED = 0x6c766c75;
const SPARK_WAIT_MS = 500;
const SPARK_LIFE_MS = 800;
const SPARK_SPEED_FROM = 20;
const SPARK_SPEED_SPAN = 20;
const SPARK_RISE = 20;
const SPARK_SQUASH = 0.7;
const words = new Uint32Array(2);

/**
 * A number from 0 up to 1 for one number of a spark's flight, from the shared hash.
 *
 * @param index The spark.
 * @param which Which number of its flight.
 * @returns The unit float.
 */
function unit(index: number, which: number): number {
  words[0] = index;
  words[1] = which;
  return toUnitFloat(hashWords(SPARK_SEED, words, 2));
}

/**
 * The sparks' flights: each leaves at its own wait of up to 500 ms and sprays out along its angle and up.
 *
 * @returns One path per spark.
 */
function sprayPaths(): SparkPath[] {
  const paths: SparkPath[] = [];
  for (let index = 0; index < MAX_SPARKS; index += 1) {
    const angle = unit(index, 0) * 2 * Math.PI;
    const speed = SPARK_SPEED_FROM + unit(index, 1) * SPARK_SPEED_SPAN;
    paths.push({
      delayMs: Math.floor(unit(index, 2) * SPARK_WAIT_MS),
      lifeMs: SPARK_LIFE_MS,
      x0: 0,
      y0: 0,
      x1: Math.cos(angle) * speed,
      y1: Math.sin(angle) * speed * SPARK_SQUASH - SPARK_RISE,
      light: index % 3 === 0 ? 1 : 0,
    });
  }
  return paths;
}

/**
 * The `levelUp` definition.
 */
export const LEVEL_UP: VfxDefinition = Object.freeze({
  id: "levelUp",
  lifeMs: 1800,
  target: "character",
  others: true,
  sound: Object.freeze({ own: Stinger.LevelUp, others: LevelUpSound.Seen }),
  layers: Object.freeze<VfxLayer[]>([
    { kind: "ring", fromMs: 0, toMs: 200, radiusFrom: 18, radiusTo: 4, bright: false },
    { kind: "ring", fromMs: 200, toMs: 700, radiusFrom: 8, radiusTo: 44, bright: true },
    {
      kind: "aura",
      keys: [
        [0, 0],
        [150, 0.4],
        [150, 0.7],
        [500, 0.7],
        [1100, 0],
      ],
      peak: 0.7,
    },
    { kind: "pillar", fromMs: 200, growMs: 120, holdUntilMs: 800, endMs: 1100, half: 9 },
    { kind: "rays", fromMs: 200, spanMs: 500, count: MAX_RAYS, inner: [6, 32], outer: [12, 46] },
    { kind: "sparks", fromMs: 200, spanMs: SPARK_WAIT_MS + SPARK_LIFE_MS, armsUntil: [0.5, 0.8], paths: sprayPaths() },
    { kind: "text", word: "hud.levelUp.sign", fromMs: 400, riseMs: 300, risePx: 10, toMs: 1700, gap: [1560, 1620] },
    { kind: "edge", fromMs: 200, toMs: 1300 },
  ]),
  reduced: Object.freeze({ lifeMs: 900, flashMs: 300, pillarHalf: 9, auraStep: 2, edgeStep: 2, pulseStep: 0, textStill: true }),
});
